import React, { useState } from "react";
import { ChecklistItem, DEFAULT_CHECKLIST_OPTIONS, makeChecklistItem } from "../shared/types";
import * as S from "../shared/styles";
import FlagColumnPicker from "./FlagColumnPicker";
import { SourceColumnsState } from "./useSourceColumns";

// ----------------------------  Helpers  ----------------------------

function reorder(list: ChecklistItem[]): ChecklistItem[] {
  return list.map((item, i) => ({ ...item, display_order: i }));
}

const optionsOf = (item: ChecklistItem): string[] => item.options ?? DEFAULT_CHECKLIST_OPTIONS;

// ----------------------------  ChecklistBuilder  ----------------------------
// The checklist questions, each with its own buttons and help text, and where the
// checklist shows: on every record / object (no flags), or only where a flag column is true.

interface ChecklistBuilderProps {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
  enableFlags: string[];
  onEnableFlagsChange: (flags: string[]) => void;
  // pickable sample columns (from the backend); flags are its boolean columns
  sourceColumns: SourceColumnsState;
}

const ChecklistBuilder: React.FC<ChecklistBuilderProps> = ({
  items,
  onChange,
  enableFlags,
  onEnableFlagsChange,
  sourceColumns,
}) => {
  // No flags = every record / object. Unticking only opens the picker; nothing
  // picked there still saves as every record / object.
  const [picking, setPicking] = useState(false);
  const showAll = !picking && enableFlags.length === 0;
  const toggleShowAll = (checked: boolean) => {
    setPicking(!checked);
    if (checked) onEnableFlagsChange([]);
  };
  const add = () => onChange([...items, makeChecklistItem({ display_order: items.length })]);
  const update = (id: string, patch: Partial<ChecklistItem>) =>
    onChange(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const remove = (id: string) => onChange(reorder(items.filter((i) => i.id !== id)));
  const moveItem = (id: string, dir: number) => {
    const idx = items.findIndex((i) => i.id === id);
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange(reorder(copy));
  };

  const setOption = (item: ChecklistItem, k: number, value: string) =>
    update(item.id, { options: optionsOf(item).map((o, i) => (i === k ? value : o)) });
  const addOption = (item: ChecklistItem) => update(item.id, { options: [...optionsOf(item), ""] });
  const removeOption = (item: ChecklistItem, k: number) =>
    update(item.id, { options: optionsOf(item).filter((_, i) => i !== k) });

  return (
    <div>
      {items.map((item, idx) => (
        <div key={item.id} style={itemCard}>
          <div style={S.checklistRow}>
            <span style={S.checklistIndex}>{idx + 1}.</span>
            <input
              style={{ ...S.formInput, flex: 1 }}
              value={item.label}
              onChange={(e) => update(item.id, { label: e.target.value })}
              placeholder="Checklist question"
            />
            <button
              style={{ ...S.fieldCardActionBtn, opacity: idx === 0 ? 0.3 : 1 }}
              disabled={idx === 0}
              onClick={() => moveItem(item.id, -1)}
              title="Move up"
            >
              ↑
            </button>
            <button
              style={{ ...S.fieldCardActionBtn, opacity: idx === items.length - 1 ? 0.3 : 1 }}
              disabled={idx === items.length - 1}
              onClick={() => moveItem(item.id, 1)}
              title="Move down"
            >
              ↓
            </button>
            <button style={S.fieldCardRemoveBtn} onClick={() => remove(item.id)} title="Remove">
              ✕
            </button>
          </div>
          <input
            style={{ ...S.formInput, marginTop: "6px" }}
            value={item.help_text ?? ""}
            onChange={(e) => update(item.id, { help_text: e.target.value })}
            placeholder="Help text (optional, shown on hover)"
          />
          <div style={{ marginTop: "8px" }}>
            <label style={S.formLabel}>Buttons</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
              {optionsOf(item).map((o, k) => (
                <span key={k} style={optionChip}>
                  <input
                    style={optionInput}
                    value={o}
                    onChange={(e) => setOption(item, k, e.target.value)}
                    placeholder="Button label"
                    aria-label={`Button ${k + 1} of ${item.label || "question " + (idx + 1)}`}
                  />
                  <button
                    style={S.fieldCardRemoveBtn}
                    onClick={() => removeOption(item, k)}
                    title="Remove button"
                  >
                    ✕
                  </button>
                </span>
              ))}
              <button style={S.btnOutline} onClick={() => addOption(item)}>
                + Button
              </button>
            </div>
          </div>
        </div>
      ))}
      <button style={{ ...S.btnOutline, width: "100%" }} onClick={add}>
        + Add Checklist Item
      </button>

      {/* Where the checklist shows */}
      <div style={{ marginTop: "16px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
          <input
            type="checkbox"
            style={S.formCheckbox}
            checked={showAll}
            onChange={(e) => toggleShowAll(e.target.checked)}
          />
          <span style={{ fontSize: "0.875rem", color: "#374151" }}>
            Show the checklist on every record / object
          </span>
        </label>
        {!showAll && (
          <div style={{ marginTop: "8px", paddingLeft: "24px" }}>
            <FlagColumnPicker
              value={enableFlags}
              onChange={onEnableFlagsChange}
              sourceColumns={sourceColumns}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const itemCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px 10px",
  marginBottom: "8px",
};

const optionChip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
};

const optionInput: React.CSSProperties = {
  ...S.formInput,
  width: "8rem",
};

export default ChecklistBuilder;
